import { useState, useMemo, useEffect } from "react";
import axios from "axios";
import { useQuery } from "@tanstack/react-query";
import useAuth from "../../../hooks/useAuth";
import { apiVariable } from "../../../api/apiVariable";
import { Box, CircularProgress, Typography, TextField } from "@mui/material";
import "../DriverTable/DriverTable.css";

import { OwnedUnitDto } from "../../../components/Fleet/OwnedUnitsDrawer/OwnedUnitsDrawer";
import { GridSortModel } from "@mui/x-data-grid";
import { useNavigate, useSearchParams } from "react-router-dom";
import { companyTableColumns } from "../../../data/FormColumns/Fleet/companyTableColumns";
import PersistedDataGrid from "../../PersistedDataGrid/PersistedDataGrid";

interface CompanyDto {
  id: number;
  name: string;
  managerName: string;
  managerPhoneNumber: string;
  billingContactName: string;
  billingContactPhoneNumber: string;
  billingEmailAddress: string;
  defaultPaymentMethod: string;
  taxId: string;
  dotNumber: string;
  mcNumber: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  state: string;
  zipCode: string;
  ownedUnits: OwnedUnitDto[];
}

const fetchCompanies = async (token?: string) => {
  const { data } = await axios.get<CompanyDto[]>(
    `${apiVariable}/displayOwnerCompanies`,
    { headers: { Authorization: `Bearer ${token}` } },
  );
  return data;
};

const CompanyTable = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialSearch = searchParams.get("search") ?? "";
  const [searchTerm, setSearchTerm] = useState(initialSearch);
  const [sortModel, setSortModel] = useState<GridSortModel>([
    {
      field: "rentedAssetsCount",
      sort: "desc",
    },
  ]);

  const {
    data: companies,
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey: ["companies"],
    queryFn: () => fetchCompanies(token!),
  });

  useEffect(() => {
    if (searchTerm) {
      setSearchParams({ search: searchTerm });
    } else {
      setSearchParams({});
    }
  }, [searchTerm, setSearchParams]);

  useEffect(() => {
    if (token) refetch();
  }, [token, refetch]);

  const transformed = useMemo(() => {
    if (!companies) return [];
    return companies.map((c, i) => ({
      id: `${c.id}-${i}`,
      realId: c.id,
      name: c.name,
      managerName: c.managerName,
      managerPhoneNumber: c.managerPhoneNumber,
      billingContactName: c.billingContactName,
      billingContactPhoneNumber: c.billingContactPhoneNumber,
      billingEmailAddress: c.billingEmailAddress,
      defaultPaymentMethod: c.defaultPaymentMethod,
      taxId: c.taxId,
      dotNumber: c.dotNumber,
      mcNumber: c.mcNumber,
      addressLine1: c.addressLine1,
      addressLine2: c.addressLine2,
      city: c.city,
      state: c.state,
      zipCode: c.zipCode,
      ownedUnits: c.ownedUnits,
      rentedAssetsCount: c.ownedUnits?.length || 0,
    }));
  }, [companies]);

  const filtered = useMemo(() => {
    if (!transformed.length) return [];
    return transformed.filter((row) =>
      [
        row.name,
        row.managerName,
        row.managerPhoneNumber,
        row.billingContactName,
        row.billingContactPhoneNumber,
        row.billingEmailAddress,
        row.defaultPaymentMethod,
        row.taxId,
        row.dotNumber,
        row.mcNumber,
        row.addressLine1,
        row.addressLine2,
        row.city,
        row.state,
        row.zipCode,
        ...row.ownedUnits.map((u: { unitNumber: any }) => u.unitNumber),
      ].some((f) =>
        f?.toString().toLowerCase().includes(searchTerm.toLowerCase()),
      ),
    );
  }, [searchTerm, transformed]);

  return (
    <Box className="drivers-table-container" height={900}>
      <Box
        className="table-controls"
        sx={{
          display: "flex",
          justifyContent: "right",
          alignItems: "center",
          marginBottom: 3,
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            width: { xs: "100%", sm: 400 },
            minWidth: 0,
            alignItems: "flex-end",
            position: "relative",
          }}
        >
          <TextField
            label="Search"
            variant="outlined"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search"
            sx={{ maxWidth: "100%", width: 400 }}
          />
        </Box>
      </Box>

      {isLoading ? (
        <Box className="table-loader">
          <CircularProgress />
        </Box>
      ) : isError ? (
        <Box className="table-error">
          <Typography color="error">Error: {(error as any).message}</Typography>
        </Box>
      ) : (
        <PersistedDataGrid
          tableName="companies"
          rows={filtered}
          columns={companyTableColumns}
          sortModel={sortModel}
          onSortModelChange={setSortModel}
          onRowClick={(params) => navigate(`/company/${params.row.id}/assets`)}
          sx={{ height: 700 }}
        />
      )}
    </Box>
  );
};

export default CompanyTable;
